import {
    AppstoreOutlined,
    CheckCircleOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    MoreOutlined,
    PlusOutlined,
    SwapOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal, Select, Space, Tooltip } from "antd";
import { AIGenerateTagButton } from "components";
import React, { useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";


/**
 * DataTagActionBar Molecule - Action bar component with bulk operations and view controls
 * @param {Array} selectedRowKeys - Selected row keys for bulk operations
 * @param {Function} onMultiDeleteDataTags - Function to handle bulk delete
 * @param {Function} setSelectedRowKeys - Function to clear selections
 * @param {number} totalTagsCount - Total number of tags for bulk delete text
 * @param {Object} aiProps - AI-related props for AIGenerateTagButton
 * @param {Function} onCreateNewDataTag - Function to create new data tag
 * @param {Object} permission - Permission object
 * @param {Function} onViewChange - Function to change view mode
 * @param {string} viewState - Current view state (card/list)
 */
const DataTagActionBar = ({ selectedRowKeys, onMultiDeleteDataTags, setSelectedRowKeys, aiProps, onBulkMoveDataTags, onCreateNewDataTag, permission, onViewChange, viewState }) => {
    const {
        generateTagsWithAI,
        createTags,
        isGeneratingTags,
        isCreatingTags,
        businessAreaList,
        aiGeneratedTags,
        resetGeneratedTags,
        aiGeneratedErrors,
        bulkCreateErrors,
        openAIKeyExists,
        dataTagList,
        rephraseDataTag,
        isRephrasingTags,
        rephrasedDataTag,
    } = aiProps;

    const [showBusinessAreaSelect, setShowBusinessAreaSelect] = useState(false);
    const [tempSelectedBusinessArea, setTempSelectedBusinessArea] = useState(null);
    const [showMoveConfirmModal, setShowMoveConfirmModal] = useState(false);
    const [tagsToMove, setTagsToMove] = useState([]);
    const [tagsAlreadyInArea, setTagsAlreadyInArea] = useState([]);
    const [targetBusinessArea, setTargetBusinessArea] = useState(null);

    const handleBulkMove = () => {
        setShowBusinessAreaSelect(true);
        setTempSelectedBusinessArea(null);
    };

    const handleBulkDelete = () => {
        onMultiDeleteDataTags(selectedRowKeys);
        setSelectedRowKeys([]);
    };

    const confirmBusinessAreaMove = () => {
        console.log("Temp Selected Business Area:", tempSelectedBusinessArea);
        if (tempSelectedBusinessArea) {
            const selectedTags = dataTagList.filter((tag) => selectedRowKeys.includes(tag.tagId));

            const targetArea = businessAreaList.find((ba) => ba.businessAreaId === tempSelectedBusinessArea);

            const toMove = selectedTags.filter((tag) => tag.businessAreaId !== tempSelectedBusinessArea);

            const alreadyIn = selectedTags.filter((tag) => tag.businessAreaId === tempSelectedBusinessArea);

            setTagsToMove(toMove);
            setTagsAlreadyInArea(alreadyIn);
            setTargetBusinessArea(targetArea);
            setShowMoveConfirmModal(true);
            setShowBusinessAreaSelect(false);
        }
    };

    const handleConfirmMove = () => {
        if (tagsToMove.length > 0) {
            const tagIdsToMove = tagsToMove.map((tag) => tag.tagId);
            onBulkMoveDataTags(tagIdsToMove, tempSelectedBusinessArea);
        }
        setShowMoveConfirmModal(false);
        setSelectedRowKeys([]);
        resetMoveState();
    };

    const resetMoveState = () => {
        setTempSelectedBusinessArea(null);
        setTagsToMove([]);
        setTagsAlreadyInArea([]);
        setTargetBusinessArea(null);
    };

    const cancelBusinessAreaSelect = () => {
        setShowBusinessAreaSelect(false);
        setTempSelectedBusinessArea(null);
    };

    const handleClearSelection = () => {
        setSelectedRowKeys([]);
        setShowBusinessAreaSelect(false);
        resetMoveState();
    };

    const bulkMenuItems = [
        {
            key: 'move',
            icon: <SwapOutlined className="text-blue-500" />,
            label: <span className="font-medium">Move Business Area</span>,
            onClick: handleBulkMove,
            className: "px-4 py-3 hover:bg-blue-50",
        },
        {
            type: 'divider',
        },
        {
            key: 'delete',
            icon: <DeleteOutlined className="text-red-500" />,
            label: <span className="font-medium text-red-600">Delete {selectedRowKeys.length} Tags</span>,
            onClick: handleBulkDelete,
            className: "px-4 py-3 hover:bg-red-50",
        },
    ];

    const showBulkActions = selectedRowKeys.length >= 2;

    return (
        <>
            <div className="flex items-center justify-between">
                <div className="flex flex-1 items-center space-x-4">
                    {showBulkActions ? (
                        showBusinessAreaSelect ? (
                            <div className="flex items-center space-x-3 rounded-lg border border-green-200 bg-green-50 px-4 py-2 shadow-sm">
                                <SwapOutlined className="text-green-600" />
                                <span className="text-sm font-medium text-green-700">Move {selectedRowKeys.length} tags to:</span>
                                <Select
                                    placeholder="Select business area"
                                    value={tempSelectedBusinessArea}
                                    onChange={setTempSelectedBusinessArea}
                                    style={{ width: 200 }}
                                    autoFocus
                                    showSearch={{
                                        filterOption:(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }}
                                    className="rounded-md"
                                    options={businessAreaList.map((area) => ({
                                        value: area.businessAreaId,
                                        label: area.name,
                                    }))}
                                />
                                <Space size="small">
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={confirmBusinessAreaMove}
                                        disabled={!tempSelectedBusinessArea}
                                        className="border-green-600 bg-green-600 hover:bg-green-700"
                                    >
                                        Continue
                                    </Button>
                                    <Button size="small" onClick={cancelBusinessAreaSelect}>
                                        Cancel
                                    </Button>
                                </Space>
                            </div>
                        ) : (
                            <div className="flex items-center space-x-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 shadow-sm">
                                <CheckCircleOutlined className="text-blue-600" />
                                <span className="text-sm font-medium text-blue-700">{selectedRowKeys.length} tags selected</span>

                               <Dropdown 
                                    menu={{ 
                                        items: bulkMenuItems,
                                        className: "min-w-48 rounded-lg border-0 shadow-lg"
                                    }} 
                                    placement="bottomLeft" 
                                    trigger={["click"]}
                                >
                                    <Button size="small" className="rounded-md border-blue-300 text-blue-600 hover:border-blue-400 hover:bg-blue-100">
                                        Actions <MoreOutlined />
                                    </Button>
                                </Dropdown>

                                <Button size="small" type="text" onClick={handleClearSelection} className="text-gray-500 hover:text-gray-700">
                                    Clear
                                </Button>
                            </div>
                        )
                    ) : (
                        <div className="flex items-center space-x-4">
                            <AIGenerateTagButton
                                generateTagsWithAI={generateTagsWithAI}
                                createTags={createTags}
                                isGeneratingTags={isGeneratingTags}
                                isCreatingTags={isCreatingTags}
                                businessAreaList={businessAreaList}
                                aiGeneratedTags={aiGeneratedTags}
                                resetAiGeneratedTags={resetGeneratedTags}
                                aiGeneratedErrors={aiGeneratedErrors}
                                bulkCreateErrors={bulkCreateErrors}
                                disabled={!permission.canAdd}
                                openAIKeyExists={openAIKeyExists}
                                DataTagList={dataTagList}
                                rephraseWithAI={rephraseDataTag}
                                isRephrasing={isRephrasingTags}
                                rephrasedDataTag={rephrasedDataTag}
                            />

                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={onCreateNewDataTag}
                                disabled={!permission.canAdd}
                                data-testid={CypressTestIds.DATATAGS_ADD_DATATAG_BUTTON}
                                className="rounded-lg shadow-sm transition-shadow duration-200 hover:shadow-md"
                            >
                                Add Tag
                            </Button>
                        </div>
                    )}
                </div>
                <div className="ml-6 flex items-center space-x-2">
                    <div className="flex shadow-sm">
                        <Tooltip title="List View">
                            <Button
                                icon={<UnorderedListOutlined />}
                                onClick={() => onViewChange("list")}
                                className={`rounded-l-2xl rounded-r-none p-2 hover:bg-blue-200 ${viewState !== "card" ? "bg-blue-100 text-blue-600" : ""}`}
                            />
                        </Tooltip>

                        <Tooltip title="Card View">
                            <Button
                                icon={<AppstoreOutlined />}
                                onClick={() => onViewChange("card")}
                                className={`rounded-l-none rounded-r-2xl p-2 ${viewState === "card" ? "bg-blue-100 text-blue-600" : ""}`}
                            />
                        </Tooltip>
                    </div>
                </div>
            </div>
            <Modal
                title={
                    <div className="flex items-center space-x-2">
                        <SwapOutlined className="text-blue-500" />
                        <span>Move Tags to "{targetBusinessArea?.name}"</span>
                    </div>
                }
                open={showMoveConfirmModal}
                destroyOnHidden
                maskClosable={false}
                onCancel={() => {
                    setShowMoveConfirmModal(false);
                    resetMoveState();
                }}
                width={700}
                style={{ top: 20 }}
                styles={{
                    maxHeight: "80vh",
                    overflow: "hidden",
                    padding: 0,
                }}
                footer={
                    <div className="flex items-center justify-between border-t bg-gray-50 px-6 py-4">
                        <div className="text-sm text-gray-600">
                            {tagsToMove.length} tag{tagsToMove.length !== 1 ? "s" : ""} will be moved
                            {tagsAlreadyInArea.length > 0 && <span className="ml-2 text-blue-600">• {tagsAlreadyInArea.length} already in target area</span>}
                        </div>
                        <Space>
                            <Button
                                onClick={() => {
                                    setShowMoveConfirmModal(false);
                                    resetMoveState();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button type="primary" onClick={handleConfirmMove} disabled={tagsToMove.length === 0} className="bg-blue-600 hover:bg-blue-700">
                                Move {tagsToMove.length} Tag{tagsToMove.length !== 1 ? "s" : ""}
                            </Button>
                        </Space>
                    </div>
                }
            >
                <div
                    className="space-y-6 px-6"
                    style={{
                        maxHeight: "calc(80vh - 140px)",
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    {tagsToMove.length > 0 && (
                        <div className="m-0 border-0 p-0">
                            <div className="sticky top-0 z-10 mb-4 mt-0 flex items-center justify-between border-b bg-white py-3">
                                <div className="flex items-center space-x-2">
                                    <CheckCircleOutlined className="text-green-500" />
                                    <span className="font-semibold text-green-700">Tags to be moved</span>
                                </div>
                                <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
                                    {tagsToMove.length} tag{tagsToMove.length !== 1 ? "s" : ""}
                                </span>
                            </div>

                            <div className="max-h-80 space-y-3 overflow-y-auto pr-2">
                                {tagsToMove.map((tag, index) => (
                                    <div
                                        key={tag.tagId}
                                        className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 p-4 transition-colors hover:bg-green-100"
                                    >
                                        <div className="flex min-w-0 flex-1 items-center space-x-3">
                                            <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-200 text-xs font-medium text-green-700">
                                                {index + 1}
                                            </span>
                                            <span className="truncate font-medium text-green-800" title={tag.tagName}>
                                                {tag.tagName}
                                            </span>
                                        </div>
                                        <div className="ml-4 flex flex-shrink-0 items-center space-x-2 text-sm">
                                            <span className="rounded bg-blue-100 px-2 py-1 text-xs text-blue-700">{tag.businessAreaName || "Unassigned"}</span>
                                            <SwapOutlined className="text-xs text-gray-400" />
                                            <span className="rounded bg-green-200 px-2 py-1 text-xs font-medium text-green-800">{targetBusinessArea?.name}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {tagsAlreadyInArea.length > 0 && (
                        <div className="border-t pt-6">
                            <div className="sticky top-0 z-10 mb-4 flex items-center justify-between bg-white pb-2">
                                <div className="flex items-center space-x-2">
                                    <ExclamationCircleOutlined className="text-blue-500" />
                                    <span className="font-semibold text-blue-700">Already in target area</span>
                                </div>
                                <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
                                    {tagsAlreadyInArea.length} tag{tagsAlreadyInArea.length !== 1 ? "s" : ""}
                                </span>
                            </div>

                            <div className="max-h-60 space-y-2 overflow-y-auto pr-2">
                                {tagsAlreadyInArea.map((tag, index) => (
                                    <div key={tag.tagId} className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-3">
                                        <div className="flex min-w-0 flex-1 items-center space-x-3">
                                            <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-200 text-xs text-blue-700">
                                                {index + 1}
                                            </span>
                                            <span className="truncate font-medium text-blue-800" title={tag.tagName}>
                                                {tag.tagName}
                                            </span>
                                        </div>
                                        <span className="ml-4 flex-shrink-0 rounded bg-blue-200 px-2 py-1 text-xs font-medium text-blue-800">{targetBusinessArea?.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {tagsToMove.length === 0 && tagsAlreadyInArea.length > 0 && (
                        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-6 text-center">
                            <ExclamationCircleOutlined className="mb-3 text-2xl text-yellow-600" />
                            <div className="mb-2 font-semibold text-yellow-800">No changes needed</div>
                            <p className="text-sm text-yellow-700">All selected tags are already in the target business area.</p>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default React.memo(DataTagActionBar);
